import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { AudiometrySymbols, symbolSize } from './AudiometrySymbols';
import {
  leftEarColor,
  rightEarColor,
} from './audiometryConfig';

const CustomDot = ({ cx, cy, payload, dataKey, value }) => {
  if (value === null || value === undefined) return null;

  const maskingKey = `${dataKey}_masking`;
  const maskingValue = payload[maskingKey];

  let SymbolComponent;
  let ArrowComponent = null;
  let arrowColor = null;
  let arrowTransform = '';

  if (maskingValue === 'masking') {
    switch (dataKey) {
      case 'AC_RE':
        SymbolComponent = AudiometrySymbols.AC_RE_MASKING;
        break;
      case 'AC_LE':
        SymbolComponent = AudiometrySymbols.AC_LE_MASKING;
        break;
      case 'BC_RE':
        SymbolComponent = AudiometrySymbols.BC_RE_MASKING;
        break;
      case 'BC_LE':
        SymbolComponent = AudiometrySymbols.BC_LE_MASKING;
        break;
      default:
        return null;
    }
  } else {
    switch (dataKey) {
      case 'AC_RE':
        SymbolComponent = AudiometrySymbols.AC_RE;
        break;
      case 'AC_LE':
        SymbolComponent = AudiometrySymbols.AC_LE;
        break;
      case 'BC_RE':
        SymbolComponent = AudiometrySymbols.BC_RE;
        break;
      case 'BC_LE':
        SymbolComponent = AudiometrySymbols.BC_LE;
        break;
      default:
        return null;
    }
  }

  if (maskingValue === 'no_hearing') {
    if (dataKey.includes('_RE')) {
      ArrowComponent = AudiometrySymbols.NO_RESPONSE_RE;
      arrowColor = rightEarColor;
      arrowTransform = `translate(-${symbolSize * 0.3}, ${symbolSize * 0.5})`;
    } else if (dataKey.includes('_LE')) {
      ArrowComponent = AudiometrySymbols.NO_RESPONSE_LE;
      arrowColor = leftEarColor;
      arrowTransform = `translate(${symbolSize * 0.3}, ${symbolSize * 0.5})`;
    }
  }

  return (
    <g transform={`translate(${cx - symbolSize / 2}, ${cy - symbolSize / 2})`}>
      <SymbolComponent />
      {ArrowComponent && (
        <g transform={arrowTransform}>
          <ArrowComponent color={arrowColor} />
        </g>
      )}
    </g>
  );
};

const AudiometryChart = ({
  chartRef,
  data,
  seriesConfig,
  height,
  compact = false,
}) => {
  const updatedSeriesConfig = seriesConfig.map((series) => ({
    ...series,
    dot: <CustomDot />,
  }));

  const standardMargin = { top: 20, right: 30, left: 20, bottom: 5 };
  const compactMargin = { top: 10, right: 30, left: 0, bottom: 0 };

  return (
    <div
      className={`audiometry-chart-wrapper ${compact ? 'compact' : 'standard'}`}
      style={height ? { height } : {}}
    >
      <ResponsiveContainer
        className='audiometry-chart'
        width='100%'
        height='100%'
        debounce={300}
      >
        <LineChart
          ref={chartRef}
          data={data}
          margin={compact ? compactMargin : standardMargin}
        >
          <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
          <XAxis
            dataKey='name'
            axisLine={{ stroke: '#d9d9d9' }}
            tickLine={{ stroke: '#d9d9d9' }}
            tick={{ fontSize: compact ? 10 : 12 }}
          />
          <YAxis
            domain={[-10, 120]}
            reversed={true}
            tickCount={14}
            interval={0}
            axisLine={{ stroke: '#d9d9d9' }}
            tickLine={{ stroke: '#d9d9d9' }}
            tick={{ fontSize: compact ? 10 : 12 }}
          />
          {updatedSeriesConfig.map((series) => (
            <Line
              key={series.dataKey}
              type='monotone'
              dataKey={series.dataKey}
              stroke={series.stroke}
              strokeWidth={series.dataKey.startsWith('BC_') ? 0 : 2}
              dot={series.dot}
              activeDot={series.activeDot}
              connectNulls={true}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AudiometryChart;
